/* eslint-disable @typescript-eslint/no-explicit-any -- a heterogeneous record of field
   specs cannot be typed without an open value parameter; every public surface below is
   narrowed back to the caller's own state type. */
import type { SearchSchemaInput } from '@tanstack/react-router'
import { z } from 'zod'

import { decimalToCents } from '@/lib/money'

/**
 * A schema-driven replacement for the 1,023-line `cardDuplicateFilters.ts`, which
 * hand-wrote three parallel projections of the same filter set: URL to state, state
 * to URL, and state to API. Every list re-implemented a subset of that by hand.
 *
 * Here a field declares its type once and the three projections are derived:
 *
 * - **URL to state** is `parse()`, handed to TanStack Router's `validateSearch`.
 * - **State to URL** is the router's own serializer; nothing to write.
 * - **State to API** is `toApiParams()`, which is the only place wire names,
 *   cents conversion, and date boundaries live.
 *
 * A fourth projection falls out for free: `chips()` produces the active-filter
 * summary with a reset patch per chip.
 */

export type ApiValue = string | number | boolean | string[] | number[]

export interface FilterOption<T extends string = string> {
    value: T
    label: string
    /** Shown beneath the label in the dropdown. */
    hint?: string
}

export type FilterKind = 'text' | 'enum' | 'multiEnum' | 'date' | 'number' | 'cents' | 'ratio' | 'tri' | 'flag' | 'list'

export interface FilterField<TValue> {
    kind: FilterKind
    /** The value that means "not filtering". Never written to the URL. */
    default: TValue
    schema: z.ZodType<TValue>
    /** Wire name, when it differs from the state key. */
    apiKey?: string
    /** Used by chips and by the filter controls. */
    label: string
    options?: FilterOption[]
    isEmpty: (value: TValue) => boolean
    /** `undefined` omits the parameter entirely. */
    toApi: (value: TValue) => ApiValue | undefined
    /** Chip text for a non-empty value. */
    describe: (value: TValue) => string
}

interface FieldOptions {
    label: string
    apiKey?: string
}

/** Free text. Trimmed on the way out; the trailing space an operator types is not a filter. */
export function text({ label, apiKey, max = 500 }: FieldOptions & { max?: number }): FilterField<string> {
    return {
        kind: 'text',
        default: '',
        schema: z.string().max(max).catch(''),
        apiKey,
        label,
        isEmpty: value => value.trim() === '',
        toApi: value => (value.trim() === '' ? undefined : value.trim()),
        describe: value => value.trim()
    }
}

/**
 * An id filter that only reaches the wire when it is a well-formed ObjectId.
 *
 * Several endpoints answer 400 for a malformed id, which would fail the whole list
 * rather than just that one filter. So a half-typed id filters nothing and says so
 * on its chip, and the operator sees the unfiltered list instead of an error.
 */
export function objectId({ label, apiKey }: FieldOptions): FilterField<string> {
    const valid = (value: string) => /^[a-f\d]{24}$/i.test(value.trim())

    return {
        kind: 'text',
        default: '',
        schema: z.string().max(64).catch(''),
        apiKey,
        label,
        isEmpty: value => value.trim() === '',
        toApi: value => (valid(value) ? value.trim() : undefined),
        describe: value => (valid(value) ? value.trim() : `${value.trim()} (not a valid id)`)
    }
}

/** Exactly one of a closed set, always sent. Sort fields and directions. */
export function enumOne<T extends string>(
    options: readonly FilterOption<T>[],
    defaultValue: T,
    { label, apiKey }: FieldOptions
): FilterField<T> {
    const values = options.map(option => option.value) as [T, ...T[]]
    return {
        kind: 'enum',
        default: defaultValue,
        schema: z.enum(values).catch(defaultValue) as unknown as z.ZodType<T>,
        apiKey,
        label,
        options: options as unknown as FilterOption[],
        isEmpty: value => value === defaultValue,
        toApi: value => value,
        describe: value => options.find(option => option.value === value)?.label ?? value
    }
}

/** One of a closed set, or nothing. A single-select filter with an "Any" state. */
export function enumOrAny<T extends string>(
    options: readonly FilterOption<T>[],
    { label, apiKey }: FieldOptions
): FilterField<T | ''> {
    const values = options.map(option => option.value) as [T, ...T[]]
    return {
        kind: 'enum',
        default: '',
        schema: z.enum(values).or(z.literal('')).catch('') as unknown as z.ZodType<T | ''>,
        apiKey,
        label,
        options: options as unknown as FilterOption[],
        isEmpty: value => value === '',
        toApi: value => (value === '' ? undefined : value),
        describe: value => options.find(option => option.value === value)?.label ?? value
    }
}

/**
 * How a multi-value filter reaches the wire. The API is not internally consistent
 * about this, so it is declared per field instead of remembered per call site:
 *
 * - `array` -- a plain JSON array. Correct for the POST-body list endpoints, which
 *   is most of them (deposits, withdrawals, transactions).
 * - `repeat` -- repeated query keys, for the GET endpoints (`cards/search`,
 *   `cards/rarities`). A lone value is emitted **twice**, because Express parses a
 *   single `?condition=X` as a string and the backend's `z.array()` rejects it.
 *   `$in: ['x','x']` is equivalent to `$in: ['x']` server-side. This is the
 *   `toArrayParam` quirk from the legacy client.
 * - `comma` -- one comma-joined value, which is what `cards/duplicates` expects.
 */
export type MultiEncoding = 'array' | 'repeat' | 'comma'

function encodeMulti(values: string[], encoding: MultiEncoding): ApiValue | undefined {
    if (values.length === 0) return undefined
    if (encoding === 'comma') return values.join(',')
    if (encoding === 'repeat') return values.length === 1 ? [values[0]!, values[0]!] : [...values]
    return [...values]
}

/** Multi-select over a closed set. */
export function multiEnum<T extends string>(
    options: readonly FilterOption<T>[],
    { label, apiKey, encode = 'array' }: FieldOptions & { encode?: MultiEncoding }
): FilterField<T[]> {
    const values = options.map(option => option.value) as [T, ...T[]]
    return {
        kind: 'multiEnum',
        default: [],
        schema: z.array(z.enum(values)).catch([]) as unknown as z.ZodType<T[]>,
        apiKey,
        label,
        options: options as unknown as FilterOption[],
        isEmpty: value => value.length === 0,
        toApi: value => encodeMulti(value, encode),
        describe: value => value.map(entry => options.find(option => option.value === entry)?.label ?? entry).join(', ')
    }
}

/** Free-form string list, e.g. rarities returned by the API rather than fixed in code. */
export function stringList({
    label,
    apiKey,
    encode = 'array',
    max = 50
}: FieldOptions & { encode?: MultiEncoding; max?: number }): FilterField<string[]> {
    return {
        kind: 'list',
        default: [],
        schema: z.array(z.string()).max(max).catch([]),
        apiKey,
        label,
        isEmpty: value => value.length === 0,
        toApi: value => encodeMulti(value, encode),
        describe: value => value.join(', ')
    }
}

/**
 * A calendar day in the URL (`YYYY-MM-DD`, which is what an operator can read and
 * edit by hand) expanded to an instant on the wire. `boundary` decides which end of
 * the day: a `dateTo` of the 5th must mean the end of the 5th, or the operator loses
 * a day of records without being told.
 */
export function dateDay({
    label,
    apiKey,
    boundary = 'start'
}: FieldOptions & { boundary?: 'start' | 'end' | 'raw' }): FilterField<string> {
    return {
        kind: 'date',
        default: '',
        schema: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}$/)
            .or(z.literal(''))
            .catch(''),
        apiKey,
        label,
        isEmpty: value => value === '',
        toApi: value => {
            if (value === '') return undefined
            if (boundary === 'raw') return value
            return boundary === 'end' ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`
        },
        describe: value => value
    }
}

export function number({ label, apiKey, min, max }: FieldOptions & { min?: number; max?: number }) {
    let schema = z.number()
    if (min !== undefined) schema = schema.min(min)
    if (max !== undefined) schema = schema.max(max)

    return {
        kind: 'number',
        default: undefined,
        schema: schema.optional().catch(undefined),
        apiKey,
        label,
        isEmpty: (value: number | undefined) => value === undefined || Number.isNaN(value),
        toApi: (value: number | undefined) => (value === undefined || Number.isNaN(value) ? undefined : value),
        describe: (value: number | undefined) => String(value ?? '')
    } satisfies FilterField<number | undefined>
}

/**
 * Money. The URL carries dollars because that is what the operator typed and what
 * they will read back; the wire carries integer cents because that is what every
 * amount in this API is.
 */
export function cents({ label, apiKey }: FieldOptions): FilterField<number | undefined> {
    return {
        kind: 'cents',
        default: undefined,
        schema: z.number().min(0).optional().catch(undefined),
        apiKey,
        label,
        isEmpty: value => value === undefined || Number.isNaN(value),
        toApi: value => (value === undefined || Number.isNaN(value) ? undefined : decimalToCents(value)),
        describe: value => (value === undefined ? '' : `$${value}`)
    }
}

/**
 * A score the API expresses as 0–1 and a person expresses as a percentage.
 *
 * The percentage is what lives in state and in the URL, because "match score at
 * least 85" is the sentence the operator would say, and `0.85` in a query string is
 * a value they have to stop and decode. The division happens once, here.
 */
export function ratio({ label, apiKey }: FieldOptions): FilterField<number | undefined> {
    return {
        kind: 'ratio',
        default: undefined,
        schema: z.number().min(0).max(100).optional().catch(undefined),
        apiKey,
        label,
        isEmpty: value => value === undefined || Number.isNaN(value),
        toApi: value => (value === undefined || Number.isNaN(value) ? undefined : value / 100),
        describe: value => (value === undefined ? '' : `${value}%`)
    }
}

/**
 * Yes / no / either. Three states, not a checkbox: an unchecked box cannot express
 * "show me only the ones where this is false", which is most of what these are for.
 */
export function triState({ label, apiKey }: FieldOptions): FilterField<'' | 'true' | 'false'> {
    return {
        kind: 'tri',
        default: '',
        schema: z.enum(['', 'true', 'false']).catch(''),
        apiKey,
        label,
        isEmpty: value => value === '',
        toApi: value => (value === '' ? undefined : value),
        describe: value => (value === 'true' ? 'Yes' : 'No')
    }
}

/** A switch that is either on or absent. */
export function flag({ label, apiKey }: FieldOptions): FilterField<boolean> {
    return {
        kind: 'flag',
        default: false,
        schema: z.boolean().catch(false),
        apiKey,
        label,
        isEmpty: value => !value,
        toApi: value => (value ? true : undefined),
        describe: () => 'On'
    }
}

export type FilterFields = Record<string, FilterField<any>>

export type FilterState<TFields extends FilterFields> = {
    [K in keyof TFields]: TFields[K] extends FilterField<infer V> ? V : never
}

/** Pagination is part of list URL state, so it is part of the same schema. */
export interface ListState {
    page: number
    limit: number
}

export interface FilterChip<TState> {
    /** Stable key for React, and the field it came from. */
    key: string
    label: string
    value: string
    /** Patch that clears just this chip. */
    reset: Partial<TState>
}

/**
 * The accepted URL shape: every key optional, and unknown-typed because it is coming
 * off a query string. The `SearchSchemaInput` tag is what tells TanStack Router to
 * type links against *this* rather than against `parse`'s fully-defaulted return --
 * without it, `<Link to="/users">` would have to spell out every filter.
 */
export type FilterSearchInput<TFields extends FilterFields> = {
    [K in keyof TFields]?: unknown
} & { page?: unknown; limit?: unknown } & SearchSchemaInput

export interface FilterSpec<TFields extends FilterFields> {
    fields: TFields
    defaults: FilterState<TFields> & ListState
    /** For `validateSearch`. Never throws: a bad URL degrades to defaults. */
    parse: (search: FilterSearchInput<TFields>) => FilterState<TFields> & ListState
    /** Wire params, defaults omitted, plus `page` and `limit`. */
    toApiParams: (state: FilterState<TFields> & ListState) => Record<string, ApiValue>
    chips: (state: FilterState<TFields> & ListState) => FilterChip<FilterState<TFields> & ListState>[]
    /** True when nothing is filtered, so the empty state can say so. */
    isPristine: (state: FilterState<TFields> & ListState) => boolean
    /** Patch that clears every filter and returns to page 1, keeping the page size. */
    clearPatch: () => Partial<FilterState<TFields> & ListState>
}

export interface DefineFiltersOptions {
    defaultLimit?: number
    maxLimit?: number
}

export function defineFilters<TFields extends FilterFields>(
    fields: TFields,
    { defaultLimit = 25, maxLimit = 200 }: DefineFiltersOptions = {}
): FilterSpec<TFields> {
    type State = FilterState<TFields> & ListState

    const entries = Object.entries(fields) as [keyof TFields & string, FilterField<any>][]

    const defaults = {
        page: 1,
        limit: defaultLimit,
        ...Object.fromEntries(entries.map(([key, field]) => [key, field.default]))
    } as State

    const shape: Record<string, z.ZodType<unknown>> = {
        page: z.number().int().min(1).catch(1),
        limit: z.number().int().min(1).max(maxLimit).catch(defaultLimit)
    }
    for (const [key, field] of entries) shape[key] = field.schema as z.ZodType<unknown>

    const schema = z.object(shape)

    return {
        fields,
        defaults,

        parse: search => schema.parse(search ?? {}) as State,

        toApiParams: state => {
            const params: Record<string, ApiValue> = { page: state.page, limit: state.limit }
            for (const [key, field] of entries) {
                const value = field.toApi(state[key])
                if (value === undefined) continue
                params[field.apiKey ?? key] = value
            }
            return params
        },

        chips: state =>
            entries
                .filter(([key, field]) => !field.isEmpty(state[key]))
                .map(([key, field]) => ({
                    key,
                    label: field.label,
                    value: field.describe(state[key]),
                    // Clearing a filter also returns to page 1: the page the operator
                    // was on very likely does not exist in the wider result set.
                    reset: { [key]: field.default, page: 1 } as Partial<State>
                })),

        isPristine: state => entries.every(([key, field]) => field.isEmpty(state[key])),

        clearPatch: () =>
            ({
                ...Object.fromEntries(entries.map(([key, field]) => [key, field.default])),
                page: 1
            }) as Partial<State>
    }
}
